/**
 * Speaking module introduction component.
 *
 * Introduction screen for the speaking exam module. Displays a preview of the
 * speaking interface layout with placeholder content and a countdown timer.
 * Matches the exact design of SpeakingInterface but with preview/placeholder content.
 *
 * @module SpeakingModuleIntroduction
 */

import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Play, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SpeakingWaveform } from "./SpeakingWaveform";
import { useAudioAnalyser } from "@/hooks/useAudioAnalyser";
import { cn } from "@/lib/utils";
import { buildMediaSource } from "@/utils/examSimulation";
import { logger } from "@/lib/logger";
import type { ModuleIntroduction as ModuleIntroductionType } from "@/types/exam-simulation";

interface SpeakingModuleIntroductionProps {
  readonly onStart: () => void;
  readonly introduction?: ModuleIntroductionType | null;
  readonly defaultDuration?: number; // in seconds, default 30 seconds
}

export function SpeakingModuleIntroduction({
  onStart,
  introduction = null,
  defaultDuration = 30,
}: SpeakingModuleIntroductionProps) {
  const { t } = useTranslation();
  const isAudioIntro = introduction?.material_type === "audio";
  const duration = isAudioIntro
    ? null
    : (introduction?.duration ?? defaultDuration);
  const [timeRemaining, setTimeRemaining] = useState(duration ?? 0);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [needsManualPlay, setNeedsManualPlay] = useState(false);
  const [audioEl, setAudioEl] = useState<HTMLAudioElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Drive the intro waveform with the real playing audio (flat during silence)
  const { readInto: readAudioLevels } = useAudioAnalyser({
    mediaElement: audioEl,
    enabled: isAudioPlaying,
  });

  // Countdown timer
  useEffect(() => {
    if (isAudioIntro) return;

    if (timeRemaining <= 0) {
      // Use setTimeout to avoid setState during render warning
      const timeoutId = setTimeout(() => {
        onStart();
      }, 0);
      return () => clearTimeout(timeoutId);
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, onStart, isAudioIntro]);

  // Audio intro playback
  useEffect(() => {
    if (!isAudioIntro || !introduction) return;

    const audioSrc = buildMediaSource(
      introduction.material_url,
      introduction.material_binary,
      introduction.material_mime_type,
      "audio/mpeg",
    );

    if (!audioSrc) {
      logger.warn("No audio source for speaking intro", {
        url: introduction.material_url,
        hasBinary: !!introduction.material_binary,
      });
      setNeedsManualPlay(true);
      return;
    }

    const audio = new Audio(audioSrc);
    audioRef.current = audio;
    setAudioEl(audio);
    audio.preload = "auto";
    audio.volume = 1;

    const handleEnded = () => {
      setIsAudioPlaying(false);
      onStart();
    };

    const handleError = (event: Event) => {
      const err = (event.target as HTMLAudioElement).error;
      logger.error("Speaking intro audio error", err);
      setAudioError(true);
      setIsAudioPlaying(false);
    };

    const handleCanPlay = () => {
      audio.play().catch((err) => {
        logger.warn(
          "Speaking intro autoplay blocked, awaiting manual play",
          err,
        );
        setNeedsManualPlay(true);
      });
    };

    const handlePlay = () => setIsAudioPlaying(true);
    const handlePause = () => setIsAudioPlaying(false);

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.load();

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.pause();
      audio.src = "";
      audioRef.current = null;
      setAudioEl(null);
    };
  }, [isAudioIntro, introduction, onStart]);

  const handleStartEarly = () => {
    onStart();
  };

  const handleExtendTime = () => {
    setTimeRemaining((prev) => prev + 300); // +5 minutes
  };

  const handleManualAudioPlay = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.play().catch((err) => {
        logger.error("Manual play failed for speaking intro audio", err);
        setAudioError(true);
      });
    }
  };

  const title = introduction?.title || "Speaking Module Introduction";
  const description =
    introduction?.text ||
    "Listen to the instructions and get ready to record your responses.";
  const instructions = introduction?.text
    ? introduction.text.split("\n").filter((line) => line.trim())
    : [];

  return (
    <div className="flex min-h-[100dvh] items-center justify-center overflow-y-auto bg-muted/50 px-3 py-4 transition-all duration-300 sm:px-4 sm:py-8">
      {/* Dimmed Background Overlay */}
      <div className="fixed inset-0 bg-background/60 backdrop-blur-sm" />

      <div
        className={cn(
          "relative z-10 w-full max-w-2xl mx-4 rounded-2xl bg-card shadow-2xl transition-all duration-500",
          "flex max-h-[calc(100dvh-2rem)] flex-col border-4 border-exam-prep sm:max-h-[calc(100dvh-4rem)]",
        )}
      >
        {/* Status Ring/Badge */}
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-sm font-semibold font-ui bg-exam-prep text-white whitespace-nowrap">
          {t("simulation.speaking.modes.prep")}
        </div>

        <div className="p-4 sm:p-8 pt-8 sm:pt-10 overflow-y-auto min-h-0 custom-scrollbar">
          {/* Question Progress - Show preview indicator */}
          <div className="mb-3 sm:mb-4 text-center">
            <span className="text-xs sm:text-sm text-muted-foreground font-ui">
              {isAudioIntro
                ? t("simulation.speaking.audioIntro")
                : t("simulation.speaking.preview")}
            </span>
          </div>

          {/* The Prompt / Instructions */}
          <div className="mb-6 sm:mb-8">
            <h2 className="font-reading text-base sm:text-lg font-bold text-foreground leading-relaxed text-center mb-3 sm:mb-4">
              {title}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground text-center whitespace-pre-line">
              {description}
            </p>
            {instructions.length > 1 && (
              <ul className="mt-4 space-y-2 sm:space-y-3">
                {instructions.map((line, idx) => (
                  <li
                    key={`${idx}-${line.substring(0, 10)}`}
                    className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 sm:h-2 sm:w-2 shrink-0 rounded-full bg-exam-prep" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Visualizer - Only show if audio is available */}
          {isAudioIntro &&
            introduction &&
            (introduction.material_url || introduction.material_binary) && (
              <div className="mb-6 bg-muted/30 rounded-xl p-3 sm:p-4">
                <SpeakingWaveform
                  isRecording={isAudioPlaying}
                  barColor="bg-exam-prep"
                  getLevels={readAudioLevels}
                />
              </div>
            )}

          {/* Action Button */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            {isAudioIntro && needsManualPlay && !audioError && (
              <Button
                size="lg"
                variant="outline"
                onClick={handleManualAudioPlay}
                className="w-full sm:w-auto border-exam-prep text-exam-prep text-sm h-14 sm:h-16"
              >
                <Play className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {t("simulation.speaking.playIntroAudio")}
              </Button>
            )}
            <Button
              size="lg"
              onClick={handleStartEarly}
              className="w-full sm:w-auto bg-exam-prep hover:bg-exam-prep/90 text-white px-8 h-14 sm:h-16 text-sm sm:text-base"
            >
              <Play className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              {isAudioIntro
                ? t("simulation.speaking.skipAndStart")
                : t("simulation.active.startEarly")}
            </Button>
            {!isAudioIntro && (
              <Button
                size="lg"
                variant="outline"
                onClick={handleExtendTime}
                className="w-full sm:w-auto border-exam-prep text-exam-prep px-6 h-14 sm:h-16 text-sm sm:text-base"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                {t("simulation.active.add5MinLabel")}
              </Button>
            )}
          </div>

          {!isAudioIntro && duration !== null && (
            <div className="mt-4 sm:mt-6 text-center text-[10px] sm:text-xs text-muted-foreground">
              {t("simulation.active.startingInSeconds", {
                seconds: timeRemaining,
              })}
            </div>
          )}

          {audioError && (
            <div className="mt-4 text-center text-[10px] sm:text-xs text-destructive">
              {t("simulation.active.audioErrorSpeaking")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
